document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".initially-hidden").forEach((el) => {
    el.classList.remove("initially-hidden");
  });

  initializeLazyImages();
  initializeThemeMeasurements();
  initializePreloader();
  initializeLenis();
});

/**
 * Lenis smooth scroll – site-wide. Connected to GSAP ScrollTrigger so
 * parallax-reveal, masonry-images and any other ScrollTrigger animations
 * stay in sync with Lenis scroll position.
 */
function initializeLenis() {
  function init() {
    if (
      typeof Lenis === "undefined" ||
      typeof gsap === "undefined" ||
      typeof ScrollTrigger === "undefined"
    ) {
      setTimeout(init, 50);
      return;
    }

    const lenis = new Lenis({
      lerp: 0.08,
      smoothWheel: true,
    });

    lenis.on("scroll", () => {
      ScrollTrigger.update();
      window.dispatchEvent(
        new CustomEvent("lenis:scroll", { detail: lenis.scroll })
      );
    });

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return lenis.scroll;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
    });

    window.lenis = lenis;
    if (document.body.classList.contains("preloader-active")) {
      lenis.stop();
    }
  }

  init();
}

// Global lazy image loader with skeleton backgrounds
function initializeLazyImages() {
  const lazyImages = document.querySelectorAll(".lazy-fade");

  if (!lazyImages.length) return;

  lazyImages.forEach((img) => {
    // Add skeleton class to wrapper
    const wrapper = img.closest(".lazy-wrapper");
    if (wrapper) {
      wrapper.classList.add("loading");
    }

    // Check if already loaded
    if (img.complete && img.naturalHeight !== 0) {
      handleImageLoad(img, wrapper);
    } else {
      // Listen for load event
      img.addEventListener("load", function () {
        handleImageLoad(img, wrapper);
      });

      // Handle error
      img.addEventListener("error", function () {
        if (wrapper) {
          wrapper.classList.remove("loading");
          wrapper.classList.add("error");
        }
      });
    }
  });
}

function handleImageLoad(img, wrapper) {
  img.classList.add("loaded");
  if (wrapper) {
    wrapper.classList.remove("loading");
    wrapper.classList.add("loaded");
  }
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function debounceImmediate(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const html = document.documentElement;

function setElementHeight(element, { heightVar, onUpdate }) {
  if (!element) return;

  const height = element.offsetHeight;

  if (heightVar) {
    html.style.setProperty(heightVar, `${height}px`);
  }

  if (typeof onUpdate === "function") {
    onUpdate(height);
  }
}

function initializeAnnouncement() {
  const announcementBar = document.querySelector(".announcement-bar");
  if (!announcementBar) return;

  const updateAnnouncement = () =>
    setElementHeight(announcementBar, {
      heightVar: "--announcement-height",
      onUpdate: (height) => {
        window.announcementHeight = height;
      },
    });

  updateAnnouncement();

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateAnnouncement);
    observer.observe(announcementBar);
  } else {
    const debouncedAnnouncement = debounce(updateAnnouncement, 300);
    window.addEventListener("resize", debouncedAnnouncement, {
      passive: true,
    });
  }
}

function initializeHeaderHeight() {
  const mainHeader = document.querySelector(".main-header");
  if (!mainHeader) return;

  const updateHeader = () =>
    setElementHeight(mainHeader, {
      heightVar: "--header-height",
      onUpdate: (height) => {
        window.headerHeight = height;
      },
    });

  updateHeader();

  const throttledHeaderUpdate = throttle(updateHeader, 16);
  window.addEventListener("scroll", throttledHeaderUpdate, {
    passive: true,
  });

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(updateHeader);
    observer.observe(mainHeader);
  } else {
    const debouncedHeaderUpdate = debounce(updateHeader, 150);
    window.addEventListener("resize", debouncedHeaderUpdate, {
      passive: true,
    });
  }
}

function initializeThemeMeasurements() {
  initializeAnnouncement();
  initializeHeaderHeight();
}

// Helper: Calculate luminance (brightness) of a color based on rgb values like this ' 255, 255 , 255 '
function getLuminance(colorStr) {
  const [r, g, b] = colorStr.split(",").map(Number);
  return (r * 299 + g * 587 + b * 114) / 1000; // Weighted luminance
}

// ============================================
// Site Preloader (GSAP timeline: content in, hold, exit, overlay slides up, logo to header)
// ============================================
function initializePreloader() {
  const preloader = document.getElementById("preloader");
  if (!preloader) return;

  // Show the preloader at most once per hour (per browser).
  try {
    const PRELOADER_KEY = "morganPreloaderLastShown";
    const PRELOADER_TTL = 60 * 60 * 1000; // 1 hour
    const last = parseInt(
      window.localStorage.getItem(PRELOADER_KEY) || "0",
      10
    );
    if (last && Date.now() - last < PRELOADER_TTL) {
      // Shown recently — skip entirely (no overlay, no scroll lock).
      // NOTE: must add .preloader--hidden, otherwise the CSS rule
      // `html:has(#preloader:not(.preloader--hidden))` keeps overflow:hidden
      // on html/body and the page stays scroll-locked.
      preloader.classList.add("preloader--hidden");
      preloader.style.display = "none";
      preloader.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("preloader-active");
      document.body.classList.remove("preloader-active");
      return;
    }
    window.localStorage.setItem(PRELOADER_KEY, String(Date.now()));
  } catch (e) {
    // localStorage unavailable (e.g. private mode) — just run it normally.
  }

  function getScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar";
    document.body.appendChild(outer);
    const inner = document.createElement("div");
    outer.appendChild(inner);
    const w = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);
    return w;
  }
  document.documentElement.style.setProperty(
    "--scrollbar-width",
    getScrollbarWidth() + "px"
  );

  document.documentElement.classList.add("preloader-active");
  document.body.classList.add("preloader-active");

  if (window.lenis && typeof window.lenis.stop === "function") {
    window.lenis.stop();
  }

  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  function runPreloaderTimeline() {
    if (typeof gsap === "undefined") {
      setTimeout(runPreloaderTimeline, 50);
      return;
    }

    const fill = preloader.querySelector("[data-preloader-fill]");
    const headerInner = preloader.querySelector("[data-preloader-header]");
    const svgImg = preloader.querySelector("[data-preloader-svg]");
    const charCells = Array.from(
      preloader.querySelectorAll("[data-preloader-char]")
    );
    const charSpans = charCells
      .map((cell) => cell.querySelector("span"))
      .filter(Boolean);
    const interiorWrap = preloader.querySelector(
      "[data-preloader-interior-wrap] p"
    );
    const designWrap = preloader.querySelector(
      "[data-preloader-design-wrap] p"
    );
    const logoPlaceholder = preloader.querySelector(
      "[data-preloader-logo-placeholder]"
    );
    const logoFixed = preloader.querySelector("[data-preloader-logo-fixed]");
    const preloaderLogo = preloader.querySelector("[data-preloader-logo]");
    const headerLogo = document.querySelector(".section-header .header__logo");
    const headerLogoImg = headerLogo ? headerLogo.querySelector("img") : null;

    function positionFixedLogo() {
      if (!logoPlaceholder || !logoFixed) return;
      const rect = logoPlaceholder.getBoundingClientRect();
      logoFixed.style.top = rect.top + "px";
      logoFixed.style.left = rect.left + "px";
      logoFixed.style.width = rect.width + "px";
      logoFixed.style.height = rect.height + "px";
      logoFixed.style.opacity = "1";
    }
    positionFixedLogo();
    requestAnimationFrame(positionFixedLogo);

    const tl = gsap.timeline();

    function onPreloaderComplete() {
      document.documentElement.classList.remove("preloader-active");
      document.body.classList.remove("preloader-active");
      preloader.classList.add("preloader--hidden");
      setTimeout(() => {
        preloader.style.display = "none";
      }, 450);
    }

    // 0–3s: progress bar fill
    tl.to(fill, { width: "100%", duration: 3, ease: "power2.out" }, 0);

    // 0–1s: Header and SVG img to Y 0 (same animation)
    if (headerInner)
      tl.to(headerInner, { y: 0, duration: 1, ease: "power2.out" }, 0);
    if (svgImg) tl.to(svgImg, { y: 0, duration: 1, ease: "power2.out" }, 0);

    // 0–1s: SIGNATURE chars to Y 0, stagger from center (A then N,T then G,U etc.)
    if (charSpans.length)
      tl.to(
        charSpans,
        {
          y: 0,
          duration: 0.8,
          stagger: { each: 0.06, from: "center" },
          ease: "power2.out",
        },
        0
      );

    // 0–1s: INTERIOR and DESIGN to X 0
    if (interiorWrap)
      tl.to(interiorWrap, { x: 0, duration: 1, ease: "power2.out" }, 0);
    if (designWrap)
      tl.to(designWrap, { x: 0, duration: 1, ease: "power2.out" }, 0);

    // 3–3.5s: Header and SVG img up, chars down, INTERIOR/DESIGN out (sub-timeline at 3s)
    const exitTl = gsap.timeline();
    const exitOpts = {
      duration: 0.5,
      ease: "power2.out",
      immediateRender: false,
    };
    if (headerInner)
      exitTl.fromTo(headerInner, { y: 0 }, { y: "-100%", ...exitOpts }, 0);
    if (svgImg) exitTl.fromTo(svgImg, { y: 0 }, { y: "-100%", ...exitOpts }, 0);
    if (charSpans.length)
      exitTl.fromTo(charSpans, { y: 0 }, { y: "100%", ...exitOpts }, 0);
    if (interiorWrap)
      exitTl.fromTo(interiorWrap, { x: 0 }, { x: "100%", ...exitOpts }, 0);
    if (designWrap)
      exitTl.fromTo(designWrap, { x: 0 }, { x: "-100%", ...exitOpts }, 0);
    tl.add(exitTl, 3);

    // 3.5s: black overlay slides up (slower); logo moves to header (slower than overlay)
    const overlayDuration = 0.8;
    const logoDuration = 1.4; // always slower than overlay
    const preloaderInner = preloader.querySelector("[data-preloader-inner]");
    if (preloaderInner) {
      tl.to(
        preloaderInner,
        {
          y: -(window.innerHeight + 20),
          duration: overlayDuration,
          ease: "power2.out",
          force3D: true,
        },
        3.5
      );
    }

    tl.add(function animateLogoToHeader() {
      if (!logoFixed || !preloaderLogo || !headerLogo) return;
      const destEl = headerLogoImg || headerLogo;
      const destRect = destEl.getBoundingClientRect();

      gsap.to(logoFixed, {
        top: destRect.top,
        left: destRect.left,
        width: destRect.width,
        height: destRect.height,
        duration: logoDuration,
        ease: "power2.out",
        onComplete: function () {
          document.documentElement.classList.remove("preloader-active");
          document.body.classList.remove("preloader-active");
          logoFixed.style.display = "none";
          preloader.classList.add("preloader--hidden");
          if (window.lenis && typeof window.lenis.start === "function") {
            window.lenis.start();
          }
          setTimeout(function () {
            preloader.style.display = "none";
          }, 450);
        },
      });
      gsap.to(preloaderLogo, {
        scale: 1,
        duration: logoDuration,
        ease: "power2.out",
      });
    }, 3.5);
  }

  runPreloaderTimeline();
}

// Scroll to Top Button
function initializeScrollToTop() {
  const scrollToTopBtn = document.getElementById("scrollToTop");
  if (!scrollToTopBtn) return;

  const outerElement = scrollToTopBtn.querySelector(".scroll-to-top__outer");
  let lastScrollTop = 0;

  function handleScroll() {
    const scrollTop =
      window.lenis != null
        ? window.lenis.scroll
        : window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    const scrollProgress =
      documentHeight > 0 ? (scrollTop / documentHeight) * 360 : 0;

    // Show/hide button based on scroll direction
    if (scrollTop > 100) {
      scrollToTopBtn.classList.add("visible");
    } else {
      scrollToTopBtn.classList.remove("visible");
    }

    // Update progress border fill on outer element
    if (outerElement) {
      outerElement.style.setProperty(
        "--scroll-progress",
        `${scrollProgress}deg`
      );
    }

    lastScrollTop = scrollTop;
  }

  // Throttled scroll handler (native scroll and Lenis scroll)
  const throttledScroll = throttle(handleScroll, 16);
  window.addEventListener("scroll", throttledScroll, { passive: true });
  window.addEventListener("lenis:scroll", throttledScroll, { passive: true });

  // Initial check
  handleScroll();
  if (window.lenis) handleScroll();

  // Click handler to scroll to top (use Lenis if available for smooth scroll)
  scrollToTopBtn.addEventListener("click", () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  });
}

// Initialize scroll to top on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  initializeScrollToTop();
});
